/**
 * Referral Partner Signup Page
 * Public page where taxi drivers, tour bus operators, hotel staff can sign up
 * to earn 10% commission on referred sales
 */
import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  Car, Bus, Building, Users, Gift, DollarSign, 
  CheckCircle, Copy, ArrowRight, Phone, User, Mail, Building2
} from 'lucide-react';
import { api } from '@/utils/apiClient';

const PARTNER_TYPES = [
  { id: 'taxi', name: 'Taxi Driver', icon: Car, description: 'Individual taxi operator' },
  { id: 'tour_bus', name: 'Tour Bus Operator', icon: Bus, description: 'Tour company or bus driver' },
  { id: 'hotel', name: 'Hotel / Resort Staff', icon: Building, description: 'Concierge or front desk' },
  { id: 'other', name: 'Other', icon: Users, description: 'Other referral partner' }
];

export default function ReferralSignupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const repCode = searchParams.get('rep'); // Sales rep attribution
  
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company_name: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedType) {
      toast.error('Please select your partner type');
      return;
    }
    
    if (!formData.name || !formData.phone) {
      toast.error('Please fill in your name and phone number');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await api.post('/referral-partners/signup', {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || undefined,
        partner_type: selectedType,
        company_name: formData.company_name || undefined,
        recruited_by_rep_code: repCode || undefined
      });
      
      setSuccess(response.data);
      setStep(3);
      toast.success('Welcome to the Bulaccino Referral Program!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign up. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const copyCode = () => {
    navigator.clipboard.writeText(success?.code);
    toast.success('Code copied to clipboard!');
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-amber-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Bulaccino" className="h-10 w-auto" />
            <div>
              <h1 className="font-bold text-lg text-gray-900 dark:text-white">Bulaccino Cafe</h1>
              <p className="text-xs text-amber-600 dark:text-amber-400">Partner Program</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/referral-dashboard')}
          >
            Already a Partner? Login
          </Button>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero Section */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Earn Money Referring Tourists
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-8">
              Join our referral program and earn <span className="text-amber-600 font-bold">10% commission</span> on every customer you send our way. 
              Tourists save 5%, you earn cash!
            </p>
            
            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card className="bg-white/80 dark:bg-gray-800/80 border-amber-200 dark:border-gray-700">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Earn 10% Cash</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">On every referred sale</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/80 dark:bg-gray-800/80 border-amber-200 dark:border-gray-700">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Gift className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Customers Save 5%</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">They get a discount using your code</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white/80 dark:bg-gray-800/80 border-amber-200 dark:border-gray-700">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Cash Out Anytime</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Or use balance at the cafe</p>
                </CardContent>
              </Card>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Select Your Partner Type
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {PARTNER_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <motion.button
                    key={type.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedType(type.id);
                      setStep(2);
                    }}
                    className={`p-6 rounded-xl border-2 transition-all ${
                      selectedType === type.id
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-amber-300'
                    }`}
                  >
                    <Icon className={`w-8 h-8 mx-auto mb-3 ${
                      selectedType === type.id ? 'text-amber-600' : 'text-gray-500'
                    }`} />
                    <p className="font-medium text-gray-900 dark:text-white">{type.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{type.description}</p>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}
        
        {/* Step 2: Form */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Button 
              variant="ghost" 
              onClick={() => setStep(1)}
              className="mb-4"
            >
              ← Back
            </Button>
            
            <Card className="max-w-md mx-auto bg-white dark:bg-gray-800">
              <CardHeader className="text-center">
                <CardTitle>Complete Your Registration</CardTitle>
                <CardDescription>
                  You selected: {PARTNER_TYPES.find(t => t.id === selectedType)?.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="w-4 h-4" /> Full Name *
                    </Label>
                    <Input
                      id="name"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="referral-signup-name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4" /> Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="referral-signup-phone"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email (Optional)
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="referral-signup-email"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="company" className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> Company Name (Optional)
                    </Label>
                    <Input
                      id="company"
                      placeholder="Enter company name if applicable"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      data-testid="referral-signup-company"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    disabled={isSubmitting}
                    data-testid="referral-signup-submit"
                  >
                    {isSubmitting ? 'Signing up...' : 'Sign Up & Get My Code'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}
        
        {/* Step 3: Success */}
        {step === 3 && success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Welcome to the Team, {success.name}!
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8">
              Your referral code is ready. Share it with tourists and start earning!
            </p>
            
            <Card className="max-w-md mx-auto bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 border-amber-300 dark:border-amber-700 mb-8">
              <CardContent className="p-8">
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-2 uppercase tracking-wide">Your Referral Code</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-4xl font-bold text-amber-800 dark:text-amber-200 tracking-wider">
                    {success.code}
                  </span>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={copyCode}
                    className="border-amber-400"
                    data-testid="copy-referral-code"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md mx-auto mb-8">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">How It Works:</h3>
              <ol className="text-left space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 text-amber-600 font-semibold">1</span>
                  Give your code <span className="font-semibold text-amber-600">{success.code}</span> to tourists
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 text-amber-600 font-semibold">2</span>
                  They tell the cashier your code at checkout → they get 5% off
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 text-amber-600 font-semibold">3</span>
                  You earn 10% commission automatically credited to your account
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 text-amber-600 font-semibold">4</span>
                  Request cash payout (min $25) or use balance at the cafe
                </li>
              </ol>
            </div>
            
            <Button 
              onClick={() => navigate('/referral-dashboard')}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="go-to-dashboard"
            >
              Go to My Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </motion.div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
        <p>© {new Date().getFullYear()} Bulaccino Cafe. All rights reserved.</p>
      </footer>
    </div>
  );
}
